import { useEffect, useRef, useState, Suspense } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useOutlet } from "@/contexts/OutletContext";
import { Button } from "@/components/ui/button";
import { Building2, Loader2, LockKeyhole } from "lucide-react";
import AdminSidebar from "@/components/admin_dashboard/AdminSidebar";
import AdminLayout from "@/components/admin_dashboard/AdminLayout";
import { SidebarProvider } from "@/components/ui/sidebar";
import OnboardingTour from "@/components/onboarding/OnboardingTour";
import { useOnboardingTour } from "@/hooks/useOnboardingTour";
import { DYNAMIC_PRICING_ENABLED, PROGRAMME_MANAGEMENT_ENABLED } from "@/config/featureFlags";
import DibsBookingsWorkspace from "@/components/admin_dashboard/DibsBookingsWorkspace";
import KnowledgeBase from "@/components/admin_dashboard/KnowledgeBase";
import StorefrontDynamicPricing from "@/components/admin_dashboard/StorefrontDynamicPricing";
import GeneralCustomers from "@/components/admin_dashboard/GeneralCustomers";
import CustomerImportPage from "@/components/admin_dashboard/CustomerImportPage";
import GeneralAnalytics from "@/components/admin_dashboard/GeneralAnalytics";
import HomeChat from "@/components/admin_dashboard/HomeChat";
import SettingsPage from "@/components/admin_dashboard/SettingsPage";
import { useTerminology } from "@/contexts/TerminologyContext";
import AccountBillingAndPlan from "@/components/admin_dashboard/AccountBillingAndPlan";
import BookingsAddonGate from "@/components/admin_dashboard/BookingsAddonGate";
import StaffAccessCard from "@/components/admin_dashboard/StaffAccessCard";
import Conversations from "@/components/admin_dashboard/Conversations";
import StorefrontAI from "@/components/admin_dashboard/StorefrontAI";
import ActionCenter from "@/components/admin_dashboard/ActionCenter";
import Automations, { getWorkflowName } from "@/components/admin_dashboard/Automations";
import { AdminChatProvider } from "@/contexts/AdminChatContext";
import CampaignsAdmin from "@/components/admin_dashboard/CampaignsAdmin";
import CustomerGraphDebug from "@/components/admin_dashboard/CustomerGraphDebug";
import CustomerDetails from "@/components/admin_dashboard/CustomerDetails";
import CrisisInbox from "@/components/admin_dashboard/CrisisInbox";
import { StaffAccessMonitor } from "@/components/admin_dashboard/StaffAccessMonitor";
import CollaboratorSessionBanner from "@/components/admin_dashboard/CollaboratorSessionBanner";
import PlanFeatureGate from "@/components/admin_dashboard/PlanFeatureGate";
import { planHasFeature } from "@/lib/planEntitlements";
import * as Sentry from "@sentry/react";
import { ErrorFallback } from "@/components/ErrorFallback";
import { legacySectionParams, preserveAdminDevSubdomain } from "@/lib/adminOrigin";
import TrialExpiredPaymentGate from "@/components/admin_dashboard/TrialExpiredPaymentGate";
import AccountAccessGate from "@/components/onboarding/AccountAccessGate";
import { TRIAL_PAYWALL_ENABLED } from "@/config/featureFlags";
import { useSubscription } from "@/hooks/useSubscription";
import { shouldBlockForExpiredTrial } from "@/lib/trialAccess";
import {
  getSettingsPanelLabel,
  resolveSettingsPanel,
} from "@/components/admin_dashboard/settingsNavigation";

interface AdminProps {
  /** Keeps the first-run tour behind other onboarding UI that temporarily
   * renders the dashboard, such as the onboarding-success modal. */
  suppressOnboardingTour?: boolean;
}

const DEFAULT_ADMIN_SECTION = "inbox";

const ASK_DIBS_EXAMPLES = [
  "How are my bookings trending this month?",
  "Which customers haven't visited in a while?",
  "Recap this week's conversations",
];

/** What Ask Dibs AI looks like, shown behind the upgrade card. */
const AskDibsPreview = () => (
  <div className="mx-auto flex max-w-2xl flex-col items-center gap-6 px-4 py-16 text-center">
    <h2 className="font-heading text-2xl font-semibold text-foreground">Ask Dibs about your business</h2>
    <div className="flex flex-wrap justify-center gap-2">
      {ASK_DIBS_EXAMPLES.map((example) => (
        <span key={example} className="rounded-full border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground">
          {example}
        </span>
      ))}
    </div>
    <div className="h-12 w-full rounded-xl border border-border bg-card" />
  </div>
);

const Admin = ({ suppressOnboardingTour = false }: AdminProps) => {
  const {
    user,
    loading,
    isAdmin,
    ownsBusiness,
    isStaff,
    isSupportCollaborator,
    hasBookingsAddon,
    onboardingComplete,
    refreshBusinessContext,
    signOut,
    subscriptionTier,
  } = useAuth();
  const {
    subscriptionStatus,
    stripeCustomerId,
    nextBillingDate,
    loading: subscriptionLoading,
    refresh: refreshSubscription,
  } = useSubscription();
  const { isAllOutlets } = useOutlet();
  const { customer, customers } = useTerminology();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab")
    || legacySectionParams(searchParams)?.get("tab")
    || DEFAULT_ADMIN_SECTION;
  const requestedInitialSection = requestedTab === "customer-journey"
    ? "workflows"
    : requestedTab === "settings" && searchParams.get("settings") === "knowledge"
      ? "knowledge"
      : requestedTab;
  const [activeSection, setActiveSection] = useState(
    requestedInitialSection === "connectors" && searchParams.get("connector") !== "dibs-bookings"
      ? "settings"
      : requestedInitialSection,
  );
  // The `tab` the URL showed on the previous render. A click sets activeSection
  // immediately, but the router applies the new URL a moment later; without
  // this the effect below saw the new section with the old URL and flipped the
  // page back before flipping forward again.
  const lastUrlTab = useRef(searchParams.get("tab"));
  const preserveDevAdminOverride = useRef(
    import.meta.env.DEV && searchParams.get("subdomain") === "admin",
  );
  const [workflowBuilderName, setWorkflowBuilderName] = useState<string | null>(null);
  const [workflowDetail, setWorkflowDetail] = useState<{ id: string; name: string } | null>(null);
  const activeSettingsPanel = resolveSettingsPanel(searchParams.get("settings"));
  const settingsPageTitle = getSettingsPanelLabel(activeSettingsPanel);

  // Sync activeSection when URL search params change (e.g. via navigate() from child components)
  useEffect(() => {
    // Old emails linked `?section=`; rewrite to the `?tab=` the page reads.
    const legacy = legacySectionParams(searchParams);
    if (legacy) {
      setSearchParams(legacy, { replace: true });
      return;
    }
    const section = searchParams.get("tab");
    // Old links used `?tab=customer-journey`; the section is now `workflows`.
    if (section === "customer-journey") {
      setActiveSection("workflows");
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.set("tab", "workflows");
          return preserveAdminDevSubdomain(current, next);
        },
        { replace: true },
      );
      return;
    }
    if (section === "settings" && searchParams.get("settings") === "knowledge") {
      setActiveSection("knowledge");
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.set("tab", "knowledge");
          next.delete("settings");
          return preserveAdminDevSubdomain(current, next);
        },
        { replace: true },
      );
      return;
    }
    if (section === "connectors" && searchParams.get("connector") !== "dibs-bookings") {
      setActiveSection("settings");
      setSearchParams(
        (current) => preserveAdminDevSubdomain(
          current,
          new URLSearchParams({ tab: "settings", settings: "connectors" }),
        ),
        { replace: true },
      );
      return;
    }
    const urlChanged = section !== lastUrlTab.current;
    lastUrlTab.current = section;
    if (section && section !== activeSection && urlChanged) {
      setActiveSection(section);
    }
  }, [searchParams, activeSection, setSearchParams]);

  // Child sections can manage their own search params. If one replaces the
  // query string, immediately restore the localhost-only admin marker so a
  // refresh cannot fall back to the public app.
  useEffect(() => {
    if (!preserveDevAdminOverride.current || searchParams.get("subdomain") === "admin") {
      return;
    }
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set("subdomain", "admin");
        return next;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams]);

  const activeBuilderId = searchParams.get("builder");
  useEffect(() => {
    setWorkflowBuilderName(null);
  }, [activeBuilderId]);

  const handleSectionChange = (section: string, extraParams?: Record<string, string>) => {
    setActiveSection(section);
    setSearchParams(
      (current) =>
        preserveAdminDevSubdomain(
          current,
          new URLSearchParams({ tab: section, ...extraParams }),
        ),
      { replace: true },
    );
  };

  const handleExitSettings = () => {
    handleSectionChange(DEFAULT_ADMIN_SECTION);
  };

  // Onboarding tour. It points only at the sidebar and never navigates, so
  // the page the owner was on (and anything unsaved on it) is left alone.
  const { isFirstTime, markComplete } = useOnboardingTour();
  const [tourOpen, setTourOpen] = useState(false);

  // Auto-start the tour on first login (admins only, staff are excluded inside the hook).
  useEffect(() => {
    if (isFirstTime && !suppressOnboardingTour) setTourOpen(true);
  }, [isFirstTime, suppressOnboardingTour]);

  const endTour = () => {
    setTourOpen(false);
    // Only a first-time tour needs recording; a replay leaves the flag as is.
    if (isFirstTime) void markComplete();
  };
  // Help replays the tour without touching the saved "done" flag, so closing
  // the tab mid-replay never makes it auto-start at the next login.
  const handleReplayTour = () => setTourOpen(true);

  // Keep the layout component type stable across auth/query refreshes. Defining
  // a component inside Admin caused React to remount the active page whenever
  // Admin re-rendered (including after returning to a browser tab), which wiped
  // unsaved form and composer state. A new props object only updates the stable
  // AdminLayout component; it does not replace its children.
  const adminLayoutProps = {
    showHelp: activeSection !== "settings" && (isAdmin || ownsBusiness) && !isStaff,
    onHelp: handleReplayTour,
  };

  if (loading || (ownsBusiness && !isStaff && !isSupportCollaborator && subscriptionLoading)) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <div className="text-center space-y-1">
          <p className="text-sm font-medium text-foreground">Loading your dashboard...</p>
          <p className="text-xs text-muted-foreground">Preparing everything for you</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const trialAccessBlocked = shouldBlockForExpiredTrial({
    onboardingComplete,
    ownsBusiness,
    isStaff,
    isSupportCollaborator,
    subscriptionStatus,
    stripeCustomerId,
    nextBillingDate,
  });

  if (trialAccessBlocked && !TRIAL_PAYWALL_ENABLED) {
    return <AccountAccessGate onRefreshSubscription={refreshSubscription} />;
  }

  // Staff get the product; they don't get the owner's controls. Deep links to
  // these sections are refused, not just hidden from the sidebar. The legacy
  // Dibs Bookings connector URL remains available as ordinary operational work.
  const STAFF_BLOCKED_SECTIONS = ["billing", "collaboration"];

  const renderContent = () => {
    // Dashboard content is available to platform admins, business owners (their
    // own workspace), staff, and active Dibs collaborators. `ownsBusiness` is
    // separate from `isAdmin` so owners downgraded off the global admin role by
    // the P0 RLS fix still manage their own site. Mirrors ProtectedRoute.
    if (!isAdmin && !ownsBusiness && !isStaff && !isSupportCollaborator) {
      return (
        <div className="text-center py-12">
          <h2 className="text-2xl font-heading font-bold mb-2">Access restricted</h2>
          <p className="text-muted-foreground">
            You need admin or staff privileges to manage site content.
          </p>
          <Button variant="outline" className="mt-4" onClick={() => navigate("/")}>
            Go to homepage
          </Button>
        </div>
      );
    }

    // Staff trying to reach an owner-only section, by deep link or a stale tab.
    const staffBlocked =
      isStaff &&
      (STAFF_BLOCKED_SECTIONS.includes(activeSection) ||
        activeSection === "settings" ||
        (activeSection === "connectors" &&
          searchParams.get("connector") !== "dibs-bookings"));

    if (staffBlocked) {
      return (
        <AdminLayout {...adminLayoutProps} title="Not available">
          <div className="mx-auto flex max-w-lg flex-col items-center rounded-xl border border-border bg-card px-6 py-10 text-center shadow-sm">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/10">
              <LockKeyhole className="h-5 w-5 text-primary" />
            </span>
            <h2 className="mt-4 text-base font-semibold text-foreground">
              This page is owner-only
            </h2>
            <p className="mt-2 max-w-sm text-sm leading-relaxed text-muted-foreground">
              Billing &amp; plan, connectors, and staff access are managed by the business
              owner. Ask them if you need something changed here.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="standard-action-button mt-4"
              onClick={() => handleSectionChange("home")}
            >
              Back to home
            </Button>
          </div>
        </AdminLayout>
      );
    }

    // Programme-management sections (Enrolments / Attendance / Make-ups) are
    // hidden behind a flag. A stale deep link falls back to the Bookings tab.
    if (!PROGRAMME_MANAGEMENT_ENABLED && ["enrolments", "attendance", "make-ups"].includes(activeSection)) {
      handleSectionChange("bookings");
      return null;
    }

    // Dibs Bookings add-on gate. The sidebar hides the bookings workspace when a
    // business lacks the add-on, but a deep link would otherwise render it (and
    // its storefront). Builder / no-add-on businesses get an upgrade prompt
    // instead. Contacts and connector settings stay reachable — Builder's whole
    // point is importing contacts and building profiles. Staff are exempt (they belong to
    // a paying business and have no upgrade power).
    const DIBS_BOOKINGS_SECTIONS = [
      "bookings", "enrolments", "attendance", "make-ups", "waitlist",
      "business-details", "listings", "resources", "outlets", "dynamic-pricing",
    ];
    const isBookingsWorkspaceView =
      DIBS_BOOKINGS_SECTIONS.includes(activeSection) ||
      (activeSection === "connectors" && searchParams.get("connector") === "dibs-bookings");
    if (isBookingsWorkspaceView && !hasBookingsAddon && !isStaff && !isSupportCollaborator) {
      return (
        <AdminLayout {...adminLayoutProps} title="Dibs Bookings">
          <BookingsAddonGate onUpgrade={() => handleSectionChange("settings", { settings: "billing" })} />
        </AdminLayout>
      );
    }

    const aggregateReadOnlySections = new Set(["analytics", "customers", "contacts", "workflows", "bookings"]);
    if (isAllOutlets && !aggregateReadOnlySections.has(activeSection)) {
      const allOutletsPageTitles: Record<string, string> = {
        home: "Home",
        settings: settingsPageTitle,
        billing: "Billing & plan",
        collaboration: "Staff access",
      };

      return (
        <AdminLayout {...adminLayoutProps} title={allOutletsPageTitles[activeSection] ?? "All outlets"}>
          <div className="mx-auto flex max-w-lg flex-col items-center rounded-xl border border-border bg-card px-6 py-10 text-center shadow-sm">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/10">
              <Building2 className="h-5 w-5 text-primary" />
            </span>
            <h2 className="mt-4 text-base font-semibold text-foreground">Select an outlet to make changes</h2>
            <p className="mt-2 max-w-sm text-sm leading-relaxed text-muted-foreground">
              All outlets is a read-only overview. Choose a specific outlet from the menu to open this workspace and modify its settings.
            </p>
            <div className="mt-4 inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <LockKeyhole className="h-3.5 w-3.5" />
              Editing is unavailable in this view
            </div>
          </div>
        </AdminLayout>
      );
    }

    switch (activeSection) {
      case "home":
        return (
          <AdminLayout {...adminLayoutProps} title="Home" fitViewport>
            {planHasFeature(subscriptionTier, "ask_dibs_ai") ? (
              <HomeChat onNavigateToSection={handleSectionChange} />
            ) : (
              // A static preview, not HomeChat: no assistant requests on a
              // plan without Ask Dibs AI.
              <PlanFeatureGate feature="ask_dibs_ai" variant="page">
                <AskDibsPreview />
              </PlanFeatureGate>
            )}
          </AdminLayout>
        );
      case "analytics":
        return (
          <AdminLayout {...adminLayoutProps} title="Analytics">
            <GeneralAnalytics onNavigateToSection={handleSectionChange} />
          </AdminLayout>
        );
      case "bookings":
      case "enrolments":
      case "attendance":
      case "make-ups":
      case "waitlist":
      case "business-details":
      case "listings":
      case "resources":
      case "outlets": {
        const dibsTitleMap: Record<string, string> = {
          "bookings": "Bookings",
          "enrolments": "Enrolments",
          "attendance": "Attendance",
          "make-ups": "Make-ups",
          "waitlist": "Waitlist",
          "business-details": "Storefront",
          "listings": "Listings",
          "resources": "Staff",
          "outlets": "Outlets"
        };
        return (
          <AdminLayout {...adminLayoutProps} title={dibsTitleMap[activeSection]} parent="Dibs Bookings" parentSection="bookings">
            <DibsBookingsWorkspace activeSubSection={activeSection} onSectionChange={handleSectionChange} />
          </AdminLayout>
        );
      }
      case "dynamic-pricing":
        if (!DYNAMIC_PRICING_ENABLED) {
          handleSectionChange("listings");
          return null;
        }
        return (
          <AdminLayout {...adminLayoutProps} title="Dynamic pricing">
            <StorefrontDynamicPricing />
          </AdminLayout>
        );

      case "contacts": // Backward compatibility for existing bookmarked URLs.
      case "customers":
        return (
          <AdminLayout {...adminLayoutProps} title={customers}>
            <GeneralCustomers />
          </AdminLayout>
        );
      case "customer-import":
        return (
          <AdminLayout {...adminLayoutProps} title={`Import ${customers.toLowerCase()}`} parent={customers} parentSection="customers" fitViewport>
            <CustomerImportPage />
          </AdminLayout>
        );
      case "customer-details":
        return (
          <AdminLayout {...adminLayoutProps} title={`${customer} profile`} parent={customers} parentSection="customers">
            <CustomerDetails />
          </AdminLayout>
        );
      case "connectors": {
        if (searchParams.get("connector") === "dibs-bookings") {
          return (
            <AdminLayout {...adminLayoutProps} title="Bookings" parent="Dibs Bookings" parentSection="bookings">
              <DibsBookingsWorkspace activeSubSection="bookings" onSectionChange={handleSectionChange} />
            </AdminLayout>
          );
        }
        // Legacy connector links are normalised to Settings by the URL-sync
        // effect above. Avoid briefly rendering an unrelated settings panel.
        return null;
      }
      case "workflows": {
        const builderId = searchParams.get("builder");
        const builderOpen = !!builderId;
        const selectedWorkflowId = searchParams.get("workflow");
        const workflowCanvasOpen = !builderOpen && !selectedWorkflowId;
        const workflowName = getWorkflowName(selectedWorkflowId || "")
          ?? (workflowDetail?.id === selectedWorkflowId ? workflowDetail.name : null);
        const builderName = workflowBuilderName?.trim() || "Untitled workflow";
        return (
          // The stable imported layout keeps the editor mounted while the live
          // workflow name changes on each keystroke.
          <AdminLayout
            {...adminLayoutProps}
            title={builderOpen ? "Edit" : workflowName || (selectedWorkflowId ? "Workflow" : "Workflows")}
            parent={builderOpen || selectedWorkflowId ? "Workflows" : undefined}
            parentSection={builderOpen || selectedWorkflowId ? "workflows" : undefined}
            middle={builderOpen ? builderName : undefined}
            middleSection={
              builderOpen && builderId !== "new"
                ? `workflows&workflow=${builderId}`
                : undefined
            }
            fitViewport={workflowCanvasOpen}
            flushContent={workflowCanvasOpen}
          >
            <PlanFeatureGate
              feature="automations"
              featureName="Workflows"
              variant="page"
              className={workflowCanvasOpen ? "h-full min-h-0" : undefined}
              contentClassName={workflowCanvasOpen ? "h-full" : undefined}
            >
              <Automations
                onNavigateToSection={handleSectionChange}
                onBuilderNameChange={setWorkflowBuilderName}
                onDetailChange={setWorkflowDetail}
              />
            </PlanFeatureGate>
          </AdminLayout>
        );
      }
      case "inbox":
      case "conversations": // Backward compatibility for existing links.
        return (
          <AdminLayout {...adminLayoutProps} title="Inbox" fitViewport flushContent>
            <Conversations />
          </AdminLayout>
        );
      case "crisis":
        return (
          <AdminLayout title="Crisis & recovery">
            <CrisisInbox />
          </AdminLayout>
        );
      case "agent-settings":
        return (
          <AdminLayout {...adminLayoutProps} title="Advanced settings">
            <PlanFeatureGate feature="ai_auto_reply" featureName="AI agent settings" variant="page">
              <StorefrontAI />
            </PlanFeatureGate>
          </AdminLayout>
        );
      case "campaigns":
        return (
          <AdminLayout {...adminLayoutProps} title="Campaigns">
            <PlanFeatureGate feature="ai_campaigns" featureName="Campaigns" variant="page">
              <CampaignsAdmin />
            </PlanFeatureGate>
          </AdminLayout>
        );
      case "action-center":
        return (
          <AdminLayout {...adminLayoutProps} title="Approvals">
            <ActionCenter view="queue" />
          </AdminLayout>
        );
      case "audit-logs":
        return (
          <AdminLayout {...adminLayoutProps} title="Audit logs" parent="Home" parentSection="home">
            <ActionCenter view="audit" />
          </AdminLayout>
        );
      case "graph-debug":
        return (
          <AdminLayout {...adminLayoutProps} title="Graph Debug">
            <CustomerGraphDebug />
          </AdminLayout>
        );
      case "knowledge":
        return (
          <AdminLayout {...adminLayoutProps} title="Knowledge" fitViewport>
            <KnowledgeBase />
          </AdminLayout>
        );
      case "settings":
        return (
          <AdminLayout {...adminLayoutProps} title={settingsPageTitle}>
            <SettingsPage onNavigateToSection={handleSectionChange} />
          </AdminLayout>
        );
      case "billing":
        return (
          <AdminLayout {...adminLayoutProps} title="Billing & plan">
            <AccountBillingAndPlan />
          </AdminLayout>
        );
      case "collaboration":
        return (
          <AdminLayout {...adminLayoutProps} title="Staff access">
            <StaffAccessCard onNavigateToSection={handleSectionChange} />
          </AdminLayout>
        );
      default:
        return null;
    }
  };

  return (
    <Sentry.ErrorBoundary
      fallback={({ resetError }) => <ErrorFallback resetError={resetError} />}
      showDialog={false}
    >
      {/* One assistant conversation for the whole admin shell: the Home tab and
          the docked panel in Workflows are two views of it, never two chats. */}
      <AdminChatProvider>
      <SidebarProvider>
        <StaffAccessMonitor />
        <div className="h-screen flex w-full overflow-hidden bg-background">
          <AdminSidebar
            activeSection={activeSection}
            onSectionChange={handleSectionChange}
            onExitSettings={handleExitSettings}
          />

          <div className="flex-1 flex flex-col min-w-0 bg-card">
            <CollaboratorSessionBanner />
            <main className="flex-1 min-h-0 overflow-y-auto overscroll-none">
              <Suspense fallback={
                <div className="flex justify-center items-center w-full min-h-[50vh]">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              }>
                {renderContent()}
              </Suspense>
            </main>
          </div>
        </div>

        {!suppressOnboardingTour && (
          <OnboardingTour
            open={tourOpen}
            onSkip={endTour}
            onComplete={endTour}
          />
        )}
        {trialAccessBlocked && (
          <TrialExpiredPaymentGate
            onRefreshSubscription={refreshSubscription}
            onRefreshBusinessContext={refreshBusinessContext}
            onSignOut={signOut}
          />
        )}
      </SidebarProvider>
      </AdminChatProvider>
    </Sentry.ErrorBoundary>
  );
};

export default Admin;
